import * as THREE from 'three'
import React, { useMemo, useState } from 'react'
import { useThree } from '@react-three/fiber';

import { delModel, extrudeSettings, roofPattern, textureAnisotropy} from '../../../../../../../../utils/Functions';
import { const_var, params } from '../../../../../../../../redux/reducers/reducer';
import { CSG } from '../../../../../../../../assets/libs/csg-lib';
import { controls } from '../../../../../../../../redux/reducers/common/eventReducer';
import { MaterialOuterWallNew, MaterialWainScotWallNew } from '../../../../../../CustomMaterial';
import { getSimilarTexture, textureAppliedFor } from '../../../../../../../../redux/reducers/utils';

const WallModel = () => {
    const { gl } = useThree ();

    const [isStorageLeftWall, setIsStorageLeftWall] = useState(false);

    const wallThickness = 0.005;
    const cbFrontBackWallOffset = 0.16;
    
    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false

    const textureObj = useMemo(() => {
        let similarSidingTextures = getSimilarTexture(params.wallTexture)
        let similarWainscotTextures = getSimilarTexture(params.wainscotTexture)

        const texture = const_var.wallTextureObj?.[similarSidingTextures]?.clone();
        const wainscotTexture = const_var.wallTextureObj?.[similarWainscotTextures]?.clone();

        const metalTexture = const_var.wallTextureObj?.["vertical"]?.clone();
        const lightTexture = const_var.wallTextureObj?.["vertical_light"]?.clone();
        if(metalTexture){
            if (params.wallOrientation == "vertical") metalTexture.rotation = Math.PI/-2;
            else metalTexture.rotation = 0;
        }
        if(lightTexture) lightTexture.rotation = metalTexture.rotation;
        if(texture && const_var.selSidingOption === "metal") texture.rotation = metalTexture.rotation
        if(wainscotTexture && (params.wainscotTexture === "none" || params.wainscotTexture === "vertical")) wainscotTexture.rotation = metalTexture.rotation

        return {"texture":texture,"wainscotTexture" : wainscotTexture,"metalTexture":metalTexture,"lightTexture":lightTexture};
    }, [params.p_v_w, params.wallTexture, const_var.wallTextureObj, const_var.selSidingOption, params.wallOrientation,params.wainscotTexture]);

    const texture = useMemo(() => {
        if(const_var.metalTexture){
            let texture = const_var.metalTexture.clone();
            textureAnisotropy(gl, texture,1.3, 1.3, (params.p_v_w != true) ? 0 :  Math.PI/-2);    
            return texture
        }else{
            return null
        }
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

    const legDistance = useMemo(() => {
        return (const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5
    }, [const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost,const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s]])

    const offset = useMemo(() => {
        if(params.leanR_p_d % legDistance === 0 || params.leanR_p_d - params.rightLeanStoragePosition - parseInt(params.add_storage_right) < legDistance / 2) return 0.15;
        else return 0;
    }, [params.leanR_p_d, legDistance, params.rightLeanStoragePosition, params.add_storage_right])
    
    // Storage left Wall - CSG
     const storageLeftWallModelObj = useMemo(() => {
        let mainWall;
        let innerWall;
        let wainscotWall;

        //************************* */ main wall geometry
        let verticalHorizontalPosStorageS = {"x": params.p_w / 2 + 0.03,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.leanR_p_d / -2 - 0.15}
        let verticalHorizontalRotStorageS = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let patternLengthStorageS = params.leanR_p_h + ((params.b_l_t_r_pR/12) * params.leanR_p_w) + (params.p_w_c_n ? -2.935 : 0) + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 : 0)
        let patternDepthStorageS = parseInt(params.add_storage_right) + 0.3
        
        const storageSideWallModel = new THREE.Shape();
        storageSideWallModel.moveTo(params.leanR_p_d / 2 + wallThickness * 2 - parseInt(params.add_storage_right) - offset, 0);
        storageSideWallModel.lineTo(params.leanR_p_d / 2 + wallThickness * 2 - parseInt(params.add_storage_right) - offset, params.leanR_p_h + (params.leanR_p_w - 0.05) * params.b_l_t_r_pR / 12 + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 : 0));
        storageSideWallModel.lineTo(params.leanR_p_d / 2 + cbFrontBackWallOffset, params.leanR_p_h + (params.leanR_p_w - 0.05) * params.b_l_t_r_pR / 12 + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 : 0));
        storageSideWallModel.lineTo(params.leanR_p_d / 2 + cbFrontBackWallOffset, 0);
        storageSideWallModel.closePath();

        const meshSideStorageWallModelPattern = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthStorageS, isPattern), extrudeSettings(patternDepthStorageS)));
        const meshFrontStorageInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageSideWallModel, extrudeSettings(wallThickness)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageInDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('rightLeanStorageLeft', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
    
        let meshStorageWallModel_TempS = meshSideStorageWallModelPattern.clone();
        meshStorageWallModel_TempS.position.set(verticalHorizontalPosStorageS.x,verticalHorizontalPosStorageS.y,verticalHorizontalPosStorageS.z);
        meshStorageWallModel_TempS.rotation.set(verticalHorizontalRotStorageS.x,verticalHorizontalRotStorageS.y,verticalHorizontalRotStorageS.z);
        meshStorageWallModel_TempS.updateMatrixWorld();
   
        let meshFrontStorageInnerWallModel_Temp = meshFrontStorageInnerWallModel.clone();
        meshFrontStorageInnerWallModel_Temp.rotateY(Math.PI / 2)
        meshFrontStorageInnerWallModel_Temp.position.set(params.p_w / 2 + 0.07, 0, wallThickness)
        meshFrontStorageInnerWallModel_Temp.updateMatrixWorld();

        let meshFrontStorageInDelModel_Temp = meshFrontStorageInDelModel.clone();
        meshFrontStorageInDelModel_Temp.rotateY(Math.PI / 2)
        meshFrontStorageInDelModel_Temp.position.set(params.p_w / 2 - 0.5, 0, -params.leanR_p_pos - params.rightLeanStoragePosition);
        meshFrontStorageInDelModel_Temp.updateMatrixWorld();
    
        const meshFrontStorageInnerWallModel_CSG = CSG.fromMesh(meshFrontStorageInnerWallModel_Temp);
        const meshFrontStorageInDelModel_CSG = CSG.fromMesh(meshFrontStorageInDelModel_Temp);
        const meshStorageWallModel_CSGS = CSG.fromMesh(meshStorageWallModel_TempS);
    
        const frontStorageOuterWallModel_CSG =  meshStorageWallModel_CSGS.subtract(meshFrontStorageInDelModel_CSG);
        const frontStorageInnerWallModel_CSG =  meshFrontStorageInnerWallModel_CSG.subtract(meshFrontStorageInDelModel_CSG);
   
        const CSGFrontStorageOuterWallModel = CSG.toMesh(frontStorageOuterWallModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))
        const CSGFrontStorageInnerWallModel = CSG.toMesh(frontStorageInnerWallModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))

        mainWall = CSGFrontStorageOuterWallModel.geometry;
        innerWall = CSGFrontStorageInnerWallModel.geometry;

        //************************* */ wainscot wall geometry
        let wainscotVerticalHorizontalPosStorageS = {"x": params.p_w / 2 + 0.06,"y" : 0, "z" : params.leanR_p_d / -2 - 0.1}
        let wainscotVerticalHorizontalRotStorageS = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let lengthPatternStorageS = 3
        let depthPatternStorageS = patternDepthStorageS

        const meshStorageLeftWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternStorageS), extrudeSettings(depthPatternStorageS)));

        let meshStorageLeftWainscotModel_Temp = meshStorageLeftWainscotModel.clone();
        meshStorageLeftWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosStorageS.x,wainscotVerticalHorizontalPosStorageS.y,wainscotVerticalHorizontalPosStorageS.z);
        meshStorageLeftWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotStorageS.x,wainscotVerticalHorizontalRotStorageS.y,wainscotVerticalHorizontalRotStorageS.z);
        meshStorageLeftWainscotModel_Temp.updateMatrixWorld();

        const meshStorageLeftWainscotModel_CSG = CSG.fromMesh(meshStorageLeftWainscotModel_Temp);

        const storageLeftWainscotModel_CSG = meshStorageLeftWainscotModel_CSG.subtract(meshFrontStorageInDelModel_CSG);
        const CSGStorageLeftWainscotModel = CSG.toMesh(storageLeftWainscotModel_CSG, new THREE.Matrix4())
   
        wainscotWall = CSGStorageLeftWainscotModel.geometry;

        return {mainWall, innerWall, wainscotWall}    

    }, [params.wallTexture,params.p_d,params.add_storage_check_right,params.add_storage_check_right, params.rightLeanStoragePosition, params.leanR_p_pos, params.add_storage_right, params.leanR_p_d, params.p_v_w, params.p_w, params.p_b_c_b_r_b, params.p_r_s, params.leanR_p_w, params.b_l_t_r_pR, params.leanR_p_h, params.p_w_c_n, const_var.newDoorsArray, params.p_w_c_n, isPattern])

    useMemo(() => {
        let array = const_var.newDoorsArray.filter((item) => item.userData.wallName === 'c_b_r_w' && item.height > params.leanR_p_h);
        for(let i=0; i<array.length; i++) {
            let item = array[i];
            const doorPositionX = item.positionX;
            const doorWidth = item.width
            const LeftStoragePos = params.leanR_p_pos - params.leanR_p_d / 2 + parseInt(params.add_storage_right)
            const RightStoragePos = params.leanR_p_pos - params.leanR_p_d / 2
            if((RightStoragePos >= (doorPositionX - doorWidth / 2)) && (LeftStoragePos <= (doorPositionX + doorWidth / 2))){
                setIsStorageLeftWall(true)
                return;
            } 
            setIsStorageLeftWall(false)
        }

    }, [const_var.newDoorsArray, params.leanR_p_pos, params.leanR_p_d, params.add_storage_right, params.leanR_p_h])

    return (
        <>
            { storageLeftWallModelObj?.mainWall && !isStorageLeftWall &&
                <mesh name="R_L_L_S_W" geometry={storageLeftWallModelObj?.mainWall} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"rightlean"} />
                </mesh>
            }
            {storageLeftWallModelObj?.innerWall &&
                <mesh name="storage-left" geometry={storageLeftWallModelObj?.innerWall} position={[wallThickness + 0.01, 0, 0]}>
                    <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'rightLean')} opacity={0.02}  />
                </mesh>
            }
            {/* Storage Left Front Wainscot */}
            {params.p_w_c_n == true && params.add_storage_check_right && storageLeftWallModelObj?.wainscotWall &&
                <mesh name="R_L_R_S_W_wainscot" geometry={storageLeftWallModelObj?.wainscotWall} position={[-0.005,0,0]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"rightlean"} />
                </mesh>
            }
        </>
    )
}

export default WallModel